import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { cn } from '@control-center/ui';

import { CanvasRevealEffect } from '../components/effects/CanvasRevealEffect';
import { loginWithGoogle, loginWithPassword } from '../lib/auth-client';
import { extractErrorMessage } from '../lib/errors';
import { env } from '../lib/runtime-env';
import { useAuth } from '../hooks/use-auth';
import { useAuthStore } from '../stores/useAuthStore';

type Step = 'email' | 'password' | 'success';

const REMEMBERED_EMAIL_KEY = 'cc-login-email';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function TopBar(): JSX.Element {
  return (
    <header className="fixed left-1/2 top-6 z-20 -translate-x-1/2 rounded-full border border-[#333] bg-[#1f1f1f57] px-5 py-2 backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white">
          <img src="/logo-opus-ico.png" alt="" className="h-full w-full object-cover" />
        </span>
        <span className="text-sm text-white">
          <span className="font-semibold tracking-tight text-white/95">Opus Owl</span>{' '}
          <span className="text-white/55">·</span>{' '}
          <span className="text-white/60">Centro de Controle</span>
        </span>
      </div>
    </header>
  );
}

function GoogleLogo(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function EyeIcon({ off }: { off?: boolean }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {off ? (
        <>
          <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </>
      ) : (
        <>
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

interface SignInPageProps {
  className?: string;
}

export function LoginPage({ className }: SignInPageProps = {}): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') ?? '/dashboard';
  const { isAuthenticated, isLoading: hydrating, refetch } = useAuth();
  const setTokens = useAuthStore((state) => state.setTokens);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(() =>
    typeof window === 'undefined' ? '' : (localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''),
  );
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(() =>
    typeof window === 'undefined' ? false : !!localStorage.getItem(REMEMBERED_EMAIL_KEY),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [forgotHint, setForgotHint] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const googleClientRef = useRef<ReturnType<
    NonNullable<Window['google']>['accounts']['oauth2']['initTokenClient']
  > | null>(null);

  const [initialCanvasVisible, setInitialCanvasVisible] = useState(true);
  const [reverseCanvasVisible, setReverseCanvasVisible] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !hydrating && !isTransitioning) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, hydrating, navigate, from, isTransitioning]);

  useEffect(() => {
    if (step === 'password') {
      setTimeout(() => passwordRef.current?.focus(), 350);
    }
  }, [step]);

  function persistRememberedEmail(value: string, remember: boolean): void {
    if (typeof window === 'undefined') return;
    if (remember && value) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, value);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  }

  function handleEmailBlur(): void {
    if (email && !EMAIL_RE.test(email)) {
      setEmailError('Informe um email no formato nome@dominio.com.');
    } else {
      setEmailError(null);
    }
  }

  function handleEmailSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setEmailError('Informe um email no formato nome@dominio.com.');
      return;
    }
    persistRememberedEmail(email, rememberEmail);
    setStep('password');
  }

  function runSuccessTransition(): void {
    // t=0     → começa reverse canvas (shader anima a "saída")
    // t=50ms  → some canvas inicial
    // t=900ms → mostra "Tudo certo!" + checkmark (success step)
    // t=3200ms → começa fade-to-black (overlay cobrindo tudo)
    // t=4000ms → navigate (já 100% preto, switch invisível pro user)
    setReverseCanvasVisible(true);
    setTimeout(() => setInitialCanvasVisible(false), 50);
    setTimeout(() => setStep('success'), 900);
    void refetch();
    setTimeout(() => setFadingOut(true), 3200);
    setTimeout(() => {
      setIsTransitioning(false);
      navigate(from, { replace: true });
    }, 4000);
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    setIsTransitioning(true);
    try {
      const { accessToken, refreshToken } = await loginWithPassword(email, password);
      setTokens({ accessToken, refreshToken });
      persistRememberedEmail(email, rememberEmail);
      runSuccessTransition();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Email ou senha não conferem.'));
      setIsTransitioning(false);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleResponse(accessToken: string): Promise<void> {
    setError(null);
    setGoogleLoading(true);
    setIsTransitioning(true);
    try {
      const { accessToken: jwtAccess, refreshToken, user } = await loginWithGoogle({ accessToken });
      setTokens({ accessToken: jwtAccess, refreshToken });
      if (rememberEmail) persistRememberedEmail(user.email, true);
      runSuccessTransition();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Não conseguimos validar sua conta Google.'));
      setIsTransitioning(false);
    } finally {
      setGoogleLoading(false);
    }
  }

  function handleGoogleClick(): void {
    setError(null);
    if (!env.googleClientId) {
      setError('Login com Google indisponível — falta configuração.');
      return;
    }
    if (typeof window === 'undefined' || !window.google?.accounts?.oauth2) {
      setError('Google ainda carregando, tente em alguns segundos.');
      return;
    }
    if (!googleClientRef.current) {
      googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: env.googleClientId,
        scope: 'openid email profile',
        callback: (response) => {
          if (response.access_token) {
            void handleGoogleResponse(response.access_token);
          } else if (response.error) {
            setError(`Google: ${response.error_description ?? response.error}`);
            setGoogleLoading(false);
            setIsTransitioning(false);
          }
        },
        error_callback: (e) => {
          // popup_closed, popup_failed_to_open, unknown_error
          if (e.type !== 'popup_closed') {
            setError(`Google: ${e.message ?? e.type}`);
          }
          setGoogleLoading(false);
          setIsTransitioning(false);
        },
      });
    }
    setGoogleLoading(true);
    googleClientRef.current.requestAccessToken();
  }

  function handlePasswordKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') handleBackClick();
    if (typeof event.getModifierState === 'function') {
      setCapsLockOn(event.getModifierState('CapsLock'));
    }
  }

  function handleBackClick(): void {
    setStep('email');
    setPassword('');
    setError(null);
    setShowPassword(false);
    setCapsLockOn(false);
    setForgotHint(false);
    setReverseCanvasVisible(false);
    setInitialCanvasVisible(true);
  }

  return (
    <div className={cn('relative flex min-h-screen w-full flex-col bg-black', className)}>
      {/* Fade-to-black overlay — cobre o switch de páginas pra não piscar */}
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: fadingOut ? 1 : 0 }}
        transition={{ duration: 0.8, ease: [0.2, 0.7, 0.2, 1] }}
        className="pointer-events-none absolute inset-0 z-50 bg-black"
      />

      <div className="absolute inset-0 z-0">
        {initialCanvasVisible && (
          <div className="absolute inset-0">
            <CanvasRevealEffect
              animationSpeed={3}
              containerClassName="bg-black"
              colors={[
                [255, 255, 255],
                [255, 255, 255],
              ]}
              dotSize={6}
              reverse={false}
            />
          </div>
        )}
        {reverseCanvasVisible && (
          <div className="absolute inset-0">
            <CanvasRevealEffect
              animationSpeed={4}
              containerClassName="bg-black"
              colors={[
                [255, 255, 255],
                [255, 255, 255],
              ]}
              dotSize={6}
              reverse
            />
          </div>
        )}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(0,0,0,1)_0%,_transparent_100%)]" />
        <div className="absolute left-0 right-0 top-0 h-1/3 bg-gradient-to-b from-black to-transparent" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col">
        <TopBar />

        <div className="flex flex-1 flex-col items-center justify-center px-4">
          <div className="mt-[150px] w-full max-w-sm">
            <AnimatePresence mode="wait">
              {step === 'email' && (
                <motion.div
                  key="email-step"
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="space-y-6 text-center"
                >
                  <div className="space-y-1">
                    <h1 className="text-[2.5rem] font-bold leading-[1.1] tracking-tight text-white">
                      Bem-vindo
                    </h1>
                    <p className="text-[1.8rem] font-light text-white/70">ao Control Center</p>
                  </div>

                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={handleGoogleClick}
                      disabled={googleLoading || submitting}
                      aria-label="Entrar com Google"
                      className={cn(
                        'flex w-full items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-white backdrop-blur-[2px] transition-colors',
                        googleLoading || submitting
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-white/10',
                      )}
                    >
                      <GoogleLogo />
                      <span>{googleLoading ? 'Conectando…' : 'Entrar com Google'}</span>
                    </button>

                    <div className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-white/10" />
                      <span className="text-sm text-white/40">ou</span>
                      <div className="h-px flex-1 bg-white/10" />
                    </div>

                    <form onSubmit={handleEmailSubmit} className="space-y-3">
                      <div className="relative">
                        <input
                          type="email"
                          placeholder="seu@email.com"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onBlur={handleEmailBlur}
                          aria-invalid={!!emailError}
                          aria-describedby={emailError ? 'email-error' : undefined}
                          className={cn(
                            'w-full rounded-full border px-4 py-3 pr-12 text-center text-white backdrop-blur-[1px] focus:outline-none',
                            emailError
                              ? 'border-red-400/50 focus:border-red-400/80'
                              : 'border-white/10 focus:border-white/30',
                          )}
                          required
                        />
                        <button
                          type="submit"
                          aria-label="Avançar"
                          className="group absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                        >
                          <span className="relative block h-full w-full overflow-hidden">
                            <span className="absolute inset-0 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-full">
                              →
                            </span>
                            <span className="absolute inset-0 flex -translate-x-full items-center justify-center transition-transform duration-300 group-hover:translate-x-0">
                              →
                            </span>
                          </span>
                        </button>
                      </div>

                      {emailError && (
                        <p id="email-error" className="text-left text-xs text-red-300/80">
                          {emailError}
                        </p>
                      )}

                      <label className="flex cursor-pointer select-none items-center gap-2 pl-1 text-xs text-white/50 transition-colors hover:text-white/70">
                        <input
                          type="checkbox"
                          checked={rememberEmail}
                          onChange={(e) => {
                            setRememberEmail(e.target.checked);
                            persistRememberedEmail(email, e.target.checked);
                          }}
                          className="peer sr-only"
                        />
                        <span
                          aria-hidden="true"
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded-[5px] border transition-colors',
                            rememberEmail
                              ? 'border-white/60 bg-white/80'
                              : 'border-white/20 bg-transparent',
                          )}
                        >
                          {rememberEmail && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="text-black"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </span>
                        Lembrar email neste dispositivo
                      </label>
                    </form>
                  </div>

                  <p className="pt-10 text-xs text-white/40">
                    Ao entrar, você concorda com os{' '}
                    <Link
                      to="#"
                      className="text-white/40 underline transition-colors hover:text-white/60"
                    >
                      Termos de Uso
                    </Link>
                    ,{' '}
                    <Link
                      to="#"
                      className="text-white/40 underline transition-colors hover:text-white/60"
                    >
                      Política de Privacidade
                    </Link>{' '}
                    e{' '}
                    <Link
                      to="#"
                      className="text-white/40 underline transition-colors hover:text-white/60"
                    >
                      Aviso de Cookies
                    </Link>{' '}
                    da Opus Owl.
                  </p>
                </motion.div>
              )}

              {step === 'password' && (
                <motion.div
                  key="password-step"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="space-y-6 text-center"
                >
                  <div className="space-y-1">
                    <h1 className="text-[2.5rem] font-bold leading-[1.1] tracking-tight text-white">
                      Digite sua senha
                    </h1>
                    <p className="text-[1.25rem] font-light text-white/50">{email}</p>
                  </div>

                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    {/* Campo escondido com email pra password managers associarem */}
                    <input
                      type="email"
                      name="email"
                      autoComplete="username"
                      value={email}
                      readOnly
                      hidden
                    />

                    <div className="relative">
                      <input
                        ref={passwordRef}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handlePasswordKey}
                        onKeyUp={handlePasswordKey}
                        aria-invalid={!!error}
                        className="w-full rounded-full border-[1px] border-white/10 px-4 py-3 pr-12 text-center text-white backdrop-blur-[1px] focus:border focus:border-white/30 focus:outline-none"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        aria-pressed={showPassword}
                        className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <EyeIcon off={showPassword} />
                      </button>
                    </div>

                    {capsLockOn && (
                      <p className="text-left text-xs text-amber-200/80">Caps Lock está ativo.</p>
                    )}

                    {error && (
                      <p
                        role="alert"
                        className="rounded-full border border-red-500/20 bg-red-500/10 px-4 py-2 text-xs text-red-300"
                      >
                        {error}
                      </p>
                    )}

                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={() => setForgotHint((v) => !v)}
                        className="text-white/50 transition-colors hover:text-white/70"
                      >
                        Esqueci minha senha
                      </button>
                      {forgotHint && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="pt-2 text-xs text-white/40"
                        >
                          A recuperação ainda não está disponível.{' '}
                          <a
                            href="mailto:opusowl.ltda@gmail.com?subject=Reset%20de%20senha%20Control%20Center"
                            className="underline transition-colors hover:text-white/60"
                          >
                            Fale com o admin
                          </a>{' '}
                          para resetar.
                        </motion.p>
                      )}
                    </div>

                    <div className="flex w-full gap-3">
                      <motion.button
                        type="button"
                        onClick={handleBackClick}
                        className="w-[30%] rounded-full bg-white px-8 py-3 font-medium text-black transition-colors hover:bg-white/90"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                      >
                        Voltar
                      </motion.button>
                      <motion.button
                        type="submit"
                        disabled={!password || submitting}
                        className={cn(
                          'flex-1 rounded-full border py-3 font-medium transition-all duration-300',
                          password && !submitting
                            ? 'cursor-pointer border-transparent bg-white text-black hover:bg-white/90'
                            : 'cursor-not-allowed border-white/10 bg-[#111] text-white/50',
                        )}
                      >
                        {submitting ? 'Entrando…' : 'Entrar'}
                      </motion.button>
                    </div>
                  </form>

                  <div className="pt-16">
                    <p className="text-xs text-white/40">
                      Conta <em className="font-serif italic text-white/60">Owl.</em> válida em
                      todos os sistemas do ecossistema.
                    </p>
                  </div>
                </motion.div>
              )}

              {step === 'success' && (
                <motion.div
                  key="success-step"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut', delay: 0.3 }}
                  className="space-y-6 text-center"
                >
                  <div className="space-y-1">
                    <h1 className="text-[2.5rem] font-bold leading-[1.1] tracking-tight text-white">
                      Tudo certo!
                    </h1>
                    <p className="text-[1.25rem] font-light text-white/50">Redirecionando…</p>
                  </div>

                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="py-10"
                  >
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-white to-white/70">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-black"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
